/**
 * In-memory cache for the short-lived `mlda_` agent delegation bearer (FEAT-1698 M1).
 *
 * The credential is never persisted — it lives for the tab session only and is re-minted
 * transparently when it expires or when platform-api rejects it with a 401. Concurrent callers
 * share a single in-flight mint so a burst of requests cannot fan out into duplicate mutations.
 */

/** A minted delegation, already parsed into an absolute expiry in epoch milliseconds. */
export type AgentDelegation = {
  token: string;
  expiresAtMs: number;
  scopes: readonly string[];
};

/** Mints a fresh delegation; supplied by the GraphQL-backed hook. */
export type MintAgentDelegation = () => Promise<AgentDelegation>;

/** A mint attempt that produced no usable credential (permission denied or malformed payload). */
export class AgentDelegationError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    /** Backend-localized copy safe to render; client invariant failures leave this null. */
    public readonly localizedMessage: string | null = null
  ) {
    super(message);
    this.name = 'AgentDelegationError';
  }
}

/** Re-mint this far ahead of the stated expiry so an in-flight turn cannot start on a dead token. */
const DEFAULT_EXPIRY_SKEW_MS = 30_000;

/** Caches one delegation per tab session and coalesces concurrent mints. */
export class AgentDelegationStore {
  private cached: AgentDelegation | null = null;

  private inFlight: Promise<AgentDelegation> | null = null;

  public constructor(private readonly expirySkewMs: number = DEFAULT_EXPIRY_SKEW_MS) {}

  /**
   * Returns a usable bearer token, minting one when the cache is empty, near expiry, or explicitly
   * invalidated by `forceRemint`.
   */
  public async getToken(mint: MintAgentDelegation, forceRemint = false): Promise<string> {
    if (forceRemint) {
      this.cached = null;
    }
    const cached = this.cached;
    if (cached && cached.expiresAtMs - this.expirySkewMs > Date.now()) {
      return cached.token;
    }
    if (!this.inFlight) {
      this.inFlight = this.mintOnce(mint);
    }
    const delegation = await this.inFlight;
    this.cached = delegation;
    return delegation.token;
  }

  /** Runs one mint attempt, clearing the shared in-flight slot whether it resolves or throws. */
  private async mintOnce(mint: MintAgentDelegation): Promise<AgentDelegation> {
    try {
      return await mint();
    } finally {
      this.inFlight = null;
    }
  }

  /** Drops the cached credential so the next request mints a fresh one (used on 401). */
  public invalidate(): void {
    this.cached = null;
  }
}

/** Process-wide delegation cache shared by every agent panel instance in the tab. */
export const agentDelegationStore = new AgentDelegationStore();

const agentDelegationInputSchema = z.object({
  token: z.string(),
  expiresAt: z.union([z.string(), z.number()]),
  scopes: z.array(z.string()),
});

/** Parses a mint mutation payload into a delegation, rejecting unusable tokens or expiries. */
export function parseAgentDelegation(input: z.input<typeof agentDelegationInputSchema>): AgentDelegation | null {
  const parsed = agentDelegationInputSchema.safeParse(input);
  if (!parsed.success || parsed.data.token.length === 0) {
    return null;
  }
  const expiresAtMs = new Date(parsed.data.expiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return null;
  }
  return { token: parsed.data.token, expiresAtMs, scopes: parsed.data.scopes };
}
import { z } from 'zod';
